import { Controller } from "@hotwired/stimulus";

const SCHOOL_LOOKUP_DELAY = 1000;
const CITY_LOOKUP_DELAY = 300;
const EMAIL_CONFIRM_DELAY = 300;
const CITY_SCOPED_SPECIAL_SCHOOL_CODES = [
  "bsmart_other_school",
  "bsmart_private_school",
];

export default class extends Controller {
  static values = {
    findCoursesUrl: String,
    findSpecialSchoolCitiesUrl: String,
    resolveSpecialSchoolUrl: String,
    resetTokenUrl: String,
    resetPasswordPrefix: String,
    adultAge: Number,
    otherCourseLabel: String,
    schoolNotFoundLabel: String,
    loadingSchoolLabel: String,
    specialSchoolCityLabel: String,
    specialSchoolCityPlaceholder: String,
    specialSchoolPendingCreateLabel: String,
  };

  connect() {
    this.schoolInput = this.element.querySelector("#new_school_code");
    this.specialSchoolInputs = Array.from(
      this.element.querySelectorAll("[data-special-school-input]")
    );
    this.specialSchoolShortcutButtons = Array.from(
      this.element.querySelectorAll(".special-school-shortcut")
    );
    this.coursesSelect = this.element.querySelector("#user_users_school_adoptions");
    this.loadingCourses = this.element.querySelector("#loading_school_courses");
    this.matchedSchoolName = this.element.querySelector("#matched_school_name");
    this.specialSchoolCityContainer = this.element.querySelector("#special_school_city_container");
    this.specialSchoolCityInput = this.element.querySelector("#special_school_city");
    this.specialSchoolCityOptions = this.element.querySelector("#special_school_city_options");
    this.specialSchoolCityStatus = this.element.querySelector("#special_school_city_status");
    this.specialSchoolTypeInput = this.element.querySelector("#opendata_special_school_type");
    this.specialSchoolCityIdInput = this.element.querySelector("#opendata_special_school_city_id");
    this.specialSchoolPendingCreateInput = this.element.querySelector(
      "#opendata_special_school_pending_create"
    );
    this.userEmail = this.element.querySelector("#user_email");
    this.copyResetButton = this.element.querySelector("#copy-reset-token-btn");
    this.copyConfirmationButton = this.element.querySelector("#copyConfLinkBtn");
    this.parentSection = this.element.querySelector("[data-parent]");
    this.birthdayInputs = this.element.querySelectorAll(
      '[name$="[profile_attributes][birthday]"]'
    );

    this.onSchoolInput = this.onSchoolInput.bind(this);
    this.onUserEmailInput = this.onUserEmailInput.bind(this);
    this.onCopyResetClick = this.onCopyResetClick.bind(this);
    this.onCopyConfirmationClick = this.onCopyConfirmationClick.bind(this);
    this.onBirthdayInput = this.onBirthdayInput.bind(this);
    this.onSpecialSchoolFocus = this.onSpecialSchoolFocus.bind(this);
    this.onSpecialSchoolCityInput = this.onSpecialSchoolCityInput.bind(this);

    this.schoolInput?.addEventListener("input", this.onSchoolInput);
    this.specialSchoolCityInput?.addEventListener("input", this.onSpecialSchoolCityInput);
    this.specialSchoolInputs.forEach((input) => {
      input.addEventListener("focus", this.onSpecialSchoolFocus);
      input.addEventListener("click", this.onSpecialSchoolFocus);
    });
    this.userEmail?.addEventListener("input", this.onUserEmailInput);
    this.copyResetButton?.addEventListener("click", this.onCopyResetClick);
    this.copyConfirmationButton?.addEventListener("click", this.onCopyConfirmationClick);
    this.birthdayInputs.forEach((input) => {
      input.addEventListener("change", this.onBirthdayInput);
      input.addEventListener("keyup", this.onBirthdayInput);
      this.refreshParentSection(input.value);
    });
  }

  disconnect() {
    this.schoolInput?.removeEventListener("input", this.onSchoolInput);
    this.specialSchoolInputs?.forEach((input) => {
      input.removeEventListener("focus", this.onSpecialSchoolFocus);
      input.removeEventListener("click", this.onSpecialSchoolFocus);
    });
    this.userEmail?.removeEventListener("input", this.onUserEmailInput);
    this.copyResetButton?.removeEventListener("click", this.onCopyResetClick);
    this.copyConfirmationButton?.removeEventListener("click", this.onCopyConfirmationClick);
    this.birthdayInputs?.forEach((input) => {
      input.removeEventListener("change", this.onBirthdayInput);
      input.removeEventListener("keyup", this.onBirthdayInput);
    });
    this.specialSchoolCityInput?.removeEventListener("input", this.onSpecialSchoolCityInput);

    if (this.schoolTypingTimer) clearTimeout(this.schoolTypingTimer);
    if (this.cityTypingTimer) clearTimeout(this.cityTypingTimer);
    if (this.emailTypingTimer) clearTimeout(this.emailTypingTimer);
  }

  onSpecialSchoolFocus(event) {
    this.currentSpecialSchoolInput = event.currentTarget;
  }

  applySpecialSchoolCode(event) {
    event.preventDefault();
    const code = event.currentTarget?.dataset?.schoolCode;
    const schoolType = event.currentTarget?.dataset?.schoolType || code;
    if (!code) return;

    const targetInput =
      this.schoolInput ||
      this.currentSpecialSchoolInput ||
      this.specialSchoolInputs?.[0];
    if (!targetInput) return;

    this.selectSpecialSchoolShortcut(event.currentTarget);
    this.disableSchoolCodeInputForShortcut(targetInput);

    if (this.isCityScopedSpecialSchoolCode(schoolType)) {
      this.activateCityScopedSpecialSchool(schoolType, targetInput);
      return;
    }

    this.clearCityScopedSpecialSchool();
    targetInput.value = code;
    this.syncSchoolCodeMirror(targetInput);
    targetInput.dispatchEvent(new Event("input", { bubbles: true }));
    targetInput.dispatchEvent(new Event("change", { bubbles: true }));
    this.scheduleSchoolLookup({ immediate: true });
  }

  activateCityScopedSpecialSchool(code, targetInput) {
    this.cityScopedSpecialSchoolInput = targetInput;
    this.selectedSpecialSchoolCityId = null;
    this.specialSchoolCitySuggestions = [];

    if (this.specialSchoolTypeInput) this.specialSchoolTypeInput.value = code;
    if (this.specialSchoolCityIdInput) this.specialSchoolCityIdInput.value = "";
    if (this.specialSchoolPendingCreateInput) this.specialSchoolPendingCreateInput.value = "0";
    if (this.specialSchoolCityInput) this.specialSchoolCityInput.value = "";
    if (this.specialSchoolCityOptions) this.specialSchoolCityOptions.innerHTML = "";
    this.updateSpecialSchoolCityStatus("");

    targetInput.value = "";
    this.syncSchoolCodeMirror(targetInput);
    targetInput.dispatchEvent(new Event("change", { bubbles: true }));
    this.updateMatchedSchoolName("");
    this.specialSchoolCityContainer?.classList.remove("d-none");
    this.specialSchoolCityInput?.focus();
  }

  clearCityScopedSpecialSchool() {
    this.cityScopedSpecialSchoolInput = null;
    this.selectedSpecialSchoolCityId = null;
    this.specialSchoolCitySuggestions = [];

    this.specialSchoolCityContainer?.classList.add("d-none");
    if (this.specialSchoolCityInput) this.specialSchoolCityInput.value = "";
    if (this.specialSchoolCityOptions) this.specialSchoolCityOptions.innerHTML = "";
    if (this.specialSchoolTypeInput) this.specialSchoolTypeInput.value = "";
    if (this.specialSchoolCityIdInput) this.specialSchoolCityIdInput.value = "";
    if (this.specialSchoolPendingCreateInput) this.specialSchoolPendingCreateInput.value = "0";
    this.updateSpecialSchoolCityStatus("");
  }

  selectSpecialSchoolShortcut(selectedButton) {
    this.specialSchoolShortcutButtons?.forEach((button) => {
      const selected = button === selectedButton;
      button.classList.toggle("active", selected);
      button.setAttribute("aria-pressed", selected ? "true" : "false");
    });
  }

  clearSelectedSpecialSchoolShortcut() {
    this.enableSchoolCodeInputAfterShortcut();
    this.specialSchoolShortcutButtons?.forEach((button) => {
      button.classList.remove("active");
      button.setAttribute("aria-pressed", "false");
    });
  }

  disableSchoolCodeInputForShortcut(input) {
    if (!input) return;

    this.shortcutDisabledSchoolCodeInput = input;
    input.disabled = true;
    input.dataset.specialSchoolShortcutDisabled = "true";
    this.syncSchoolCodeMirror(input);
  }

  enableSchoolCodeInputAfterShortcut() {
    const input = this.shortcutDisabledSchoolCodeInput;
    if (input?.dataset?.specialSchoolShortcutDisabled === "true") {
      input.disabled = false;
      delete input.dataset.specialSchoolShortcutDisabled;
    }
    this.shortcutDisabledSchoolCodeInput = null;
    this.removeSchoolCodeMirror();
  }

  syncSchoolCodeMirror(input) {
    if (!input?.name) return;

    const mirror = this.schoolCodeMirrorInput || this.createSchoolCodeMirror(input);
    mirror.name = input.name;
    mirror.value = input.value || "";
  }

  createSchoolCodeMirror(input) {
    const mirror = document.createElement("input");
    mirror.type = "hidden";
    mirror.dataset.specialSchoolCodeMirror = "true";
    input.insertAdjacentElement("afterend", mirror);
    this.schoolCodeMirrorInput = mirror;
    return mirror;
  }

  removeSchoolCodeMirror() {
    this.schoolCodeMirrorInput?.remove();
    this.schoolCodeMirrorInput = null;
  }

  isCityScopedSpecialSchoolCode(code) {
    return CITY_SCOPED_SPECIAL_SCHOOL_CODES.includes((code || "").toLowerCase());
  }

  onSpecialSchoolCityInput() {
    const value = (this.specialSchoolCityInput?.value || "").trim();
    const selectedCity = this.specialSchoolCitySuggestions?.find(
      (city) => city.label === value
    );

    if (selectedCity) {
      this.selectSpecialSchoolCity(selectedCity);
      return;
    }

    this.selectedSpecialSchoolCityId = null;
    if (this.specialSchoolCityIdInput) this.specialSchoolCityIdInput.value = "";
    if (this.specialSchoolPendingCreateInput) this.specialSchoolPendingCreateInput.value = "0";
    this.updateSpecialSchoolCityStatus("");
    if (this.cityTypingTimer) clearTimeout(this.cityTypingTimer);

    if (value.length < 2) {
      if (this.specialSchoolCityOptions) this.specialSchoolCityOptions.innerHTML = "";
      return;
    }

    this.cityTypingTimer = setTimeout(() => {
      this.lookupSpecialSchoolCities(value);
    }, CITY_LOOKUP_DELAY);
  }

  async lookupSpecialSchoolCities(query) {
    if (!this.hasFindSpecialSchoolCitiesUrlValue || !this.specialSchoolCityOptions) return;

    try {
      const params = new URLSearchParams({ input_value: query });
      const response = await fetch(
        `${this.findSpecialSchoolCitiesUrlValue}?${params.toString()}`,
        { headers: { Accept: "application/json" } }
      );
      if (!response.ok) throw new Error("City lookup failed");

      const cities = await response.json();
      this.specialSchoolCitySuggestions = Array.isArray(cities) ? cities : [];
      this.specialSchoolCityOptions.innerHTML = "";
      this.specialSchoolCitySuggestions.forEach((city) => {
        const option = document.createElement("option");
        option.value = city.label;
        option.dataset.cityId = city.id;
        this.specialSchoolCityOptions.appendChild(option);
      });
    } catch (_) {
      this.specialSchoolCitySuggestions = [];
      this.specialSchoolCityOptions.innerHTML = "";
    }
  }

  async selectSpecialSchoolCity(city) {
    if (!city?.id || this.selectedSpecialSchoolCityId === String(city.id)) return;

    this.selectedSpecialSchoolCityId = String(city.id);
    if (this.specialSchoolCityIdInput) this.specialSchoolCityIdInput.value = city.id;
    await this.resolveSpecialSchoolForCity(city.id);
  }

  async resolveSpecialSchoolForCity(cityId) {
    const schoolType = this.specialSchoolTypeInput?.value || "";
    const targetInput = this.cityScopedSpecialSchoolInput || this.schoolInput;
    if (!this.hasResolveSpecialSchoolUrlValue || !targetInput || !schoolType) return;

    try {
      const requestedCityId = String(cityId);
      const requestedSchoolType = schoolType;
      const params = new URLSearchParams({ city_id: cityId, school_type: schoolType });
      const response = await fetch(
        `${this.resolveSpecialSchoolUrlValue}?${params.toString()}`,
        { headers: { Accept: "application/json" } }
      );
      if (!response.ok) throw new Error("Special school resolution failed");

      const payload = await response.json();
      if (
        this.selectedSpecialSchoolCityId !== requestedCityId ||
        this.specialSchoolTypeInput?.value !== requestedSchoolType
      ) {
        return;
      }

      if (payload.pending_create) {
        targetInput.value = "";
        this.syncSchoolCodeMirror(targetInput);
        targetInput.dispatchEvent(new Event("change", { bubbles: true }));
        if (this.specialSchoolPendingCreateInput) {
          this.specialSchoolPendingCreateInput.value = "1";
        }
        const pendingCreateLabel = this.specialSchoolPendingCreateLabelValue || "";
        this.replaceCourseOptions([]);
        this.updateMatchedSchoolName("");
        this.updateSpecialSchoolCityStatus(pendingCreateLabel);
        return;
      }

      const schoolCode = payload.school_code || "";
      targetInput.value = schoolCode;
      this.syncSchoolCodeMirror(targetInput);
      targetInput.dispatchEvent(new Event("change", { bubbles: true }));

      if (this.specialSchoolPendingCreateInput) this.specialSchoolPendingCreateInput.value = "0";
      this.updateSpecialSchoolCityStatus("");
      this.updateMatchedSchoolName("");
      targetInput.dispatchEvent(new Event("input", { bubbles: true }));
      this.scheduleSchoolLookup({ immediate: true });
    } catch (_) {
      this.updateSpecialSchoolCityStatus("");
      this.updateMatchedSchoolName("");
    }
  }

  onSchoolInput(event) {
    if (!this.schoolInput || !this.coursesSelect) return;
    if (event?.isTrusted) this.clearSelectedSpecialSchoolShortcut();

    const schoolQuery = (this.schoolInput.value || "").trim();
    if (schoolQuery.length > 0) {
      this.updateMatchedSchoolName(this.loadingSchoolLabelValue || "Searching school...");
    } else {
      this.updateMatchedSchoolName("");
    }

    this.scheduleSchoolLookup();
  }

  scheduleSchoolLookup({ immediate = false } = {}) {
    if (!this.schoolInput || !this.coursesSelect) return;
    if (this.schoolTypingTimer) clearTimeout(this.schoolTypingTimer);

    if (immediate) {
      this.lookupSchoolCourses();
      return;
    }

    this.schoolTypingTimer = setTimeout(() => {
      this.lookupSchoolCourses();
    }, SCHOOL_LOOKUP_DELAY);
  }

  async lookupSchoolCourses() {
    this.loadingCourses?.classList.remove("d-none");

    try {
      const params = new URLSearchParams({ input_value: this.schoolInput.value || "" });
      const response = await fetch(`${this.findCoursesUrlValue}?${params.toString()}`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("School lookup failed");
      const payload = await response.json();
      const rows = Array.isArray(payload) ? payload : payload.courses || [];
      const schoolName = Array.isArray(payload) ? "" : payload.school_label || payload.school_name || "";
      const schoolFound = Array.isArray(payload) ? false : payload.school_found === true;
      const latestSchoolQuery = (this.schoolInput.value || "").trim();

      this.coursesSelect.innerHTML = "";
      this.replaceCourseOptions(rows);

      if (schoolName) {
        this.updateMatchedSchoolName(schoolName, "muted");
      } else if (latestSchoolQuery.length > 0 && !schoolFound) {
        this.updateMatchedSchoolName(this.schoolNotFoundLabelValue || "School not found", "danger");
      } else {
        this.updateMatchedSchoolName("", "muted");
      }
    } catch (_) {
      // Keep existing options unchanged on request errors.
      this.updateMatchedSchoolName("", "muted");
    } finally {
      this.loadingCourses?.classList.add("d-none");
    }
  }

  updateMatchedSchoolName(text, tone = "muted") {
    if (!this.matchedSchoolName) return;
    this.matchedSchoolName.classList.remove("text-muted", "text-danger");
    this.matchedSchoolName.classList.add(tone === "danger" ? "text-danger" : "text-muted");
    this.matchedSchoolName.innerText = text || "";
  }

  updateSpecialSchoolCityStatus(text) {
    if (!this.specialSchoolCityStatus) return;
    this.specialSchoolCityStatus.innerText = text || "";
  }

  replaceCourseOptions(rows) {
    if (!this.coursesSelect) return;

    this.coursesSelect.innerHTML = "";
    const sortedRows = Array.isArray(rows)
      ? rows.sort((a, b) => (a[1] || "").localeCompare(b[1] || ""))
      : [];

    const otherOption = document.createElement("option");
    otherOption.value = "other";
    otherOption.innerText = this.otherCourseLabelValue || "Altro";
    this.coursesSelect.appendChild(otherOption);

    sortedRows.forEach((row) => {
      const option = document.createElement("option");
      option.value = row[0];
      option.innerText = row[1];
      this.coursesSelect.appendChild(option);
    });
  }

  onUserEmailInput() {
    if (!this.userEmail || this.userEmail.dataset.bsmartSchoolImport !== "true") return;
    if (this.emailTypingTimer) clearTimeout(this.emailTypingTimer);

    this.emailTypingTimer = setTimeout(() => {
      const submit = this.element.querySelector('.simple_form [type="submit"]');
      if (!submit) return;

      const message = this.userEmail.dataset.textMsg;
      submit.setAttribute("data-turbo-confirm", message);
    }, EMAIL_CONFIRM_DELAY);
  }

  async onCopyResetClick() {
    try {
      const response = await fetch(this.resetTokenUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        credentials: "same-origin",
      });
      if (!response.ok) return;
      const data = await response.json();
      this.copyText(`${this.resetPasswordPrefixValue}${data.token || ""}`);
      window.location.reload();
    } catch (_) {
      // Keep the form usable when clipboard endpoint fails.
    }
  }

  onCopyConfirmationClick() {
    this.copyText(this.copyConfirmationButton?.dataset.confirmationLink || "");
  }

  onBirthdayInput(event) {
    this.refreshParentSection(event.target.value);
  }

  refreshParentSection(rawValue) {
    if (!this.parentSection || !rawValue) return;
    const birthday = new Date(rawValue);
    if (Number.isNaN(birthday.getTime())) return;

    const ageDifMs = Date.now() - birthday.getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    this.parentSection.style.display = age < this.adultAgeValue ? "block" : "none";
  }

  copyText(text) {
    if (!text) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
      return;
    }

    const temp = document.createElement("textarea");
    temp.value = text;
    temp.style.position = "fixed";
    temp.style.opacity = "0";
    document.body.appendChild(temp);
    temp.select();
    document.execCommand("copy");
    temp.remove();
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }
}
